/**
 * ================================================================
 * API SERVICE: KYCManagementService
 * ================================================================
 *
 * @service          kycApi
 * @description      Comprehensive API service for KYC management operations
 * @baseUrl          /kyc/*
 *
 * @endpoints
 * - GET    /kyc/kyc-summary - Fetch KYC summary statistics
 * - GET    /kyc/kyc-summary-details - Fetch detailed modal table data
 * - GET    /kyc/clients/kyc-details - Fetch client overview with KYC details
 * - GET    /kyc/clients/search - Search for clients
 * - GET    /kyc/clients/details - Fetch detailed client information
 * - GET    /kyc/clients/activity-tracking - Fetch activity tracking data
 * - GET    /kyc/clients/activity-details - Fetch detailed activity information
 * - GET    /kyc/applications - Fetch KYC applications
 * - GET    /kyc/modifications/list - Fetch KYC modification list
 * - GET    /kyc/get/client/modifications-details - Fetch modification details
 * - GET    /kyc/get-rekyc-status - Fetch Re-KYC status information
 * - GET    /kyc/get-ekyc-status - Fetch E-KYC status information
 * - GET    /kyc/get-rekyc-client-details - Fetch Re-KYC client details
 * - GET    /kyc/get-ekyc-client-details - Fetch E-KYC client details
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: RTK Query automatic caching with specific tags
 * - Tags: KycSummary, ModalTable, ClientOverview, ClientSearch, etc.
 * - TTL: 5-10 minutes based on data volatility
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Automatic session clearing and login redirect
 * - Network errors: User-friendly error messages
 *
 * @types
 * - Multiple interface imports from @/types/kyc/* modules
 * - ApiError: Standardized error response format (from @/types/common)
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';

// ================================================================
// TYPE IMPORTS - CENTRALIZED ORGANIZATION
// ================================================================
//
// All type imports organized by domain for better maintainability
// Using centralized index exports where available for cleaner imports
// ================================================================

// Compliance Management Types
import type { KYCModificationDetailResponse } from '@/types/compliance';

// KYC Domain Types - Centralized Import
import type {
  // Application Types
  EkycStatusListResponse,
  KycApplicationDetailResponse,
  KycApplicationResponse,
  ReKycStatusListResponse,
  // Client Details Types
  ClientDetailsQueryParams,
  ClientDetailsResponse,
  // Compliance Management Types
  BalanceSheetApiResponse,
  BalanceSheetResponse,
  ComplianceFilters,
  FileUploadRequest,
  FileUploadResponse,
  FinancialProofApiResponse,
  FinancialProofResponse,
  OtpResendRequest,
  OtpSendRequest,
  OtpSendResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  // Dashboard Tab Types
  ClientOverviewQueryParams,
  ClientOverviewResponse,
  KycSummaryResponse,
  ModalTableQueryParams,
  ModalTableResponse,
  // Activity Table Types
  ActivityDetailsQueryParams,
  ActivityDetailsResponse,
  ActivityTrackingResponse,
  // Profile Tab Types
  ClientSearchQueryParams,
  ClientSearchResponse,
} from '@/types/kyc';
import type {
  KYCModificationDownloadResponse,
  KYCModificationDownloadParams,
  KYCModificationListParams,
  KYCModificationListResponse,
} from '@/types/kyc/modification';

// ================================================================
// API URL CONSTANTS
// ================================================================
//
// Centralized API URL configuration for external compliance services
// These URLs are configured via environment variables for easy maintenance
// and deployment across different environments
// ================================================================

const BALANCE_SHEET_API_URL = import.meta.env.VITE_APP_URL_COMPLIANCE;

export const kycApi = createApi({
  reducerPath: 'kycApi',
  baseQuery: createBaseQuery(import.meta.env.VITE_API_BASE_URL),
  tagTypes: [
    'KycSummary',
    'ModalTable',
    'ClientOverview',
    'ClientSearch',
    'ClientDetails',
    'ActivityTracking',
    'ActivityDetails',
    'KycApplications',
    'KycModifications',
    'ReKycStatus',
    'EKycStatus',
    'ComplianceBalanceSheet',
    'ComplianceFinancialProof',
    'FileUpload',
    'NomineeDetails',
  ],
  endpoints: (builder) => ({
    getKycSummary: builder.query<KycSummaryResponse, number>({
      query: () => ({
        url: `/kyc/get-kyc-summary`,
        method: 'GET',
        params: {
          // hardRefresh: true,
        },
      }),
      providesTags: ['KycSummary'],
      transformErrorResponse: createApiErrorTransformer('KycSummary', true),
    }),
    getModalTable: builder.query<ModalTableResponse, ModalTableQueryParams>({
      query: ({ tag, pageNumber, pageSize, searchKey }) => ({
        url: `/kyc/get-kyc-summary-details`,
        method: 'GET',
        params: { tag, pageNumber, pageSize, searchTerm: searchKey },
      }),
      providesTags: (_, __, { tag }) => [
        { type: 'ModalTable', id: tag },
        { type: 'ModalTable', id: 'LIST' },
      ],
      keepUnusedDataFor: 0, // Keep data for 5 minutes
      transformErrorResponse: createApiErrorTransformer('KycModalTable', false),
    }),
    getClientOverview: builder.query<ClientOverviewResponse, ClientOverviewQueryParams>({
      query: ({ pageNumber, pageSize, searchKey, sortBy, sortOrder }) => ({
        url: `/kyc/clients/get-kyc-details`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          searchTerm: searchKey,
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        },
      }),
      providesTags: ['ClientOverview'],
      keepUnusedDataFor: 300,
      transformErrorResponse: createApiErrorTransformer('KycClientOverview', false),
    }),
    getClientSearch: builder.query<ClientSearchResponse, ClientSearchQueryParams>({
      query: ({ searchQuery }) => ({
        url: `/kyc/clients/search`,
        method: 'GET',
        params: { searchQuery },
      }),
      providesTags: ['ClientSearch'],
      keepUnusedDataFor: 60, // Keep search results for 1 minute
      transformErrorResponse: createApiErrorTransformer('KycClientSearch', false),
    }),
    getClientDetails: builder.query<ClientDetailsResponse, ClientDetailsQueryParams>({
      query: ({ clientId, hardRefresh = true }) => ({
        url: `/kyc/clients/get-client-details`,
        method: 'GET',
        params: { clientId, hardRefresh },
      }),
      transformErrorResponse: createApiErrorTransformer('KycClientDetails', true),
    }),
    // Nominee Details
    getNomineeDetails: builder.query<any, { clientId: string }>({
      query: ({ clientId }) => ({
        url: `/kyc/nominee-details`,
        method: 'GET',
        params: { clientId: clientId },
      }),
      providesTags: ['NomineeDetails'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch nominee details');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('NomineeDetails', true),
    }),
    // Activity Tracking
    getActivityTracking: builder.query<ActivityTrackingResponse, void>({
      query: () => ({
        url: `/kyc/clients/get-activity-tracking`,
        method: 'GET',
      }),
      providesTags: ['ActivityTracking'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch activity tracking');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycActivityTracking', true),
    }),
    // Activity Details
    getActivityDetails: builder.query<ActivityDetailsResponse, ActivityDetailsQueryParams>({
      query: ({ pageNumber, pageSize, searchTerm, sortBy, sortOrder = 'asc', status }) => ({
        url: `/kyc/clients/get-activity-details`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          ...(searchTerm && { searchTerm }),
          ...(sortBy ? { sortBy, sortOrder } : {}),
          ...(status && { status }),
        },
      }),
      providesTags: ['ActivityDetails'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch activity details');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycActivityDetails', false),
    }),
    // KYC Applications
    getKycApplications: builder.query<KycApplicationResponse, { searchKey?: string }>({
      query: ({ searchKey = '' } = {}) => ({
        url: `/kyc/applications`,
        method: 'GET',
        params: searchKey ? { searchTerm: searchKey } : undefined,
      }),
      providesTags: ['KycApplications'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch KYC applications');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycApplications', true),
    }),
    // KYC Modification List
    getKycModificationList: builder.query<
      KYCModificationListResponse | Blob,
      KYCModificationListParams
    >({
      query: ({
        branch_code = '',
        modificationType,
        kycStatus,
        pageNumber = 1,
        pageSize = 10,
        clientId = '',
        isDownload = false,
        sortBy,
        sortOrder = 'asc',
      }) => ({
        url: `/kyc/modifications/get-clients-list`,
        method: 'GET',
        params: {
          branch_code,
          modificationType,
          kycStatus,
          clientId,
          ...(isDownload ? {} : { pageNumber, pageSize }),
          ...(isDownload && { isDownload: true }),
          ...(sortBy ? { sortBy, sortOrder } : {}),
        },
        responseHandler: isDownload ? (response) => response.blob() : undefined,
      }),
      providesTags: (_, __, { modificationType, kycStatus }) => [
        { type: 'KycModifications', id: `${modificationType}-${kycStatus}` },
        { type: 'KycModifications', id: 'LIST' },
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any, _meta, arg) => {
        if (arg?.isDownload) {
          return response as Blob;
        }
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch modification list');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycModificationList', false),
    }),
    // KYC Modification Detail
    getKycModificationDetail: builder.query<
      KYCModificationDetailResponse,
      { tag: string; id: string; modificationType: string }
    >({
      query: ({ tag, id, modificationType }) => ({
        url: `/kyc/get/client/get-client-modifications-details`,
        method: 'GET',
        params: { tag, id, modificationType },
      }),
      providesTags: (_, __, { id }) => [{ type: 'KycModifications', id: `detail-${id}` }],
      keepUnusedDataFor: 600, // Keep detail data longer
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch modification details');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycModificationDetail', true),
    }),
    downloadKycModificationLogs: builder.query<
      KYCModificationDownloadResponse,
      KYCModificationDownloadParams
    >({
      query: ({
        branch_code = '',
        clientId = '',
        fromDate,
        kycStatus,
        modificationType,
        toDate,
      }) => ({
        url: `/kyc/modifications/get-clients-list-download`,
        method: 'GET',
        params: {
          branch_code,
          clientId,
          ...(fromDate ? { fromDate } : {}),
          kycStatus,
          modificationType,
          ...(toDate ? { toDate } : {}),
        },
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: KYCModificationDownloadResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to download modification logs');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('DownloadKycModificationLogs', true),
    }),
    // KYC reKYC Status
    getReKycStatus: builder.query<
      ReKycStatusListResponse,
      {
        searchTerm?: string;
        reKycStatus?: string;
        type?: string;
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: ({
        searchTerm = '',
        reKycStatus = '',
        type = '',
        pageNumber = 1,
        pageSize = 5,
      } = {}) => ({
        url: `/kyc/get-rekyc-status`,
        method: 'GET',
        params: {
          ...(searchTerm ? { searchTerm } : {}),
          ...(reKycStatus ? { reKycStatus } : {}),
          ...(type ? { type } : {}),
          pageNumber,
          pageSize,
        },
      }),
      providesTags: (_, __, { reKycStatus, type }) => [
        { type: 'ReKycStatus', id: `${reKycStatus}-${type}` },
        { type: 'ReKycStatus', id: 'LIST' },
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch Re-KYC status');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ReKycStatus', false),
    }),
    // KYC E-KYC Status
    getEKycStatus: builder.query<
      EkycStatusListResponse,
      { searchTerm?: string; ekycStatus?: string; pageNumber?: number; pageSize?: number }
    >({
      query: ({ searchTerm = '', ekycStatus = '', pageNumber = 1, pageSize = 5 } = {}) => ({
        url: `/kyc/get-ekyc-status`,
        method: 'GET',
        params: {
          ...(searchTerm ? { searchTerm } : {}),
          ...(ekycStatus ? { ekycStatus } : {}),
          pageNumber,
          pageSize,
        },
      }),
      providesTags: (_, __, { ekycStatus }) => [
        { type: 'EKycStatus', id: ekycStatus },
        { type: 'EKycStatus', id: 'LIST' },
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch E-KYC status');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('EKycStatus', false),
    }),
    // KYC reKYC Client Details
    getReKycClientDetails: builder.query<
      KycApplicationDetailResponse,
      { type: string; clientId: string }
    >({
      query: ({ type, clientId }) => ({
        url: `/kyc/get-rekyc-client-details`,
        method: 'GET',
        params: { type, clientId },
      }),
      providesTags: (_, __, { clientId }) => [{ type: 'ReKycStatus', id: `detail-${clientId}` }],
      keepUnusedDataFor: 600,
      transformResponse: (response: KycApplicationDetailResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch Re-KYC client details');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ReKycClientDetails', true),
    }),
    // KYC E-KYC Client Details
    getEKycClientDetails: builder.query<
      KycApplicationDetailResponse,
      { type: string; panNo: string; source?: string; id?: string }
    >({
      query: ({ type, panNo, source, id }) => ({
        url: `/kyc/get-ekyc-client-details`,
        method: 'GET',
        params: {
          type,
          panNo,
          source,
          id,
        },
      }),
      providesTags: (_, __, { panNo }) => [{ type: 'EKycStatus', id: `detail-${panNo}` }],
      keepUnusedDataFor: 600,
      transformResponse: (response: KycApplicationDetailResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch E-KYC client details');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('EKycClientDetails', true),
    }),

    // ================================================================
    // COMPLIANCE MANAGEMENT ENDPOINTS
    // ================================================================

    // Balance Sheet Compliance - New API Integration
    getBalanceSheetCompliance: builder.query<BalanceSheetResponse, ComplianceFilters>({
      query: ({ pageNumber = 1, pageSize = 5, searchKey, status }) => ({
        url: `${BALANCE_SHEET_API_URL}/balance-sheet/get-client-financial-status`,
        method: 'GET',
        params: {
          page: pageNumber,
          limit: pageSize,
          source: 'ap',
          ...(searchKey && { searchTerm: searchKey }),
          ...(status && { status }),
        },
      }),
      providesTags: ['ComplianceBalanceSheet'],
      keepUnusedDataFor: 300,
      transformResponse: (response: BalanceSheetApiResponse): BalanceSheetResponse => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to fetch balance sheet compliance data');
        }

        // Transform API response to match our expected format
        return {
          data: response?.data?.data || [],
          message: response.message,
          success: response.status === 200,
          pagination: {
            currentPage: response?.data?.page,
            pageSize: response?.data?.limit,
            totalItems: response?.data?.total,
            totalPages: Math.ceil(response?.data?.total / response?.data?.limit),
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceBalanceSheet', false),
    }),

    // Financial Proof Compliance
    getFinancialProofCompliance: builder.query<FinancialProofResponse, ComplianceFilters>({
      query: ({
        pageNumber = 1,
        pageSize = 10,
        searchKey,
        status,
        priority,
        clientCode,
        documentSubType,
      }) => ({
        url: `${BALANCE_SHEET_API_URL}/finproof/list-clients-status-wise`,
        method: 'GET',
        params: {
          page: pageNumber,
          limit: pageSize,
          source: 'ap',
          ...(searchKey && { searchTerm: searchKey }),
          ...(status && { status }),
          ...(priority && { priority }),
          ...(clientCode && { clientCode }),
          ...(documentSubType && { documentSubType }),
        },
      }),
      providesTags: ['ComplianceFinancialProof'],
      keepUnusedDataFor: 300,
      transformResponse: (response: FinancialProofApiResponse): FinancialProofResponse => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to fetch financial proof compliance data');
        }

        // Transform API response to match our expected format
        return {
          data: response?.data?.data || [],
          message: response.message,
          success: response.status === 200,
          filters: response?.data?.filters,
          pagination: {
            currentPage: response?.data?.pagination?.page,
            pageSize: response?.data?.pagination?.limit,
            totalItems: response?.data?.pagination?.total,
            totalPages: response?.data?.pagination?.pages,
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceFinancialProof', false),
    }),

    // File Upload for Compliance Documents
    uploadComplianceDocument: builder.mutation<FileUploadResponse, FileUploadRequest>({
      query: ({ clientCode, documentType, file, remarks }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('clientCode', clientCode);
        formData.append('documentType', documentType);
        if (remarks) {
          formData.append('remarks', remarks);
        }

        return {
          url: `${BALANCE_SHEET_API_URL}/finproof/upload-financial-proof?source=app`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, __, { documentType }) => [
        documentType === 'balance_sheet' ? 'ComplianceBalanceSheet' : 'ComplianceFinancialProof',
        'FileUpload',
      ],
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to upload document');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceUploadDocument', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          handleApiSuccess(
            'ComplianceUploadDocument',
            data?.message || 'Document uploaded successfully.',
            true,
          );
        } catch {}
      },
    }),

    // Balance Sheet File Upload - New API Integration
    uploadBalanceSheetDocument: builder.mutation<
      any,
      { file: File; financialYear?: string; clientCode?: string }
    >({
      query: ({ file, financialYear, clientCode }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `${BALANCE_SHEET_API_URL}/balance-sheet/upload-financial-proof`,
          method: 'POST',
          body: formData,
          params: {
            financialYear,
            source: 'ap',
            clientCode,
          },
        };
      },
      invalidatesTags: ['ComplianceBalanceSheet', 'FileUpload'],
      transformResponse: (response: any) => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to upload balance sheet document');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceUploadBalanceSheet', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('ComplianceUploadBalanceSheet', 'Balance sheet uploaded successfully.');
        } catch {}
      },
    }),

    // Download Compliance Document
    downloadComplianceDocument: builder.query<Blob, { documentId: string; clientCode: string }>({
      query: ({ documentId, clientCode }) => ({
        url: `/kyc/compliance/download-document`,
        method: 'GET',
        params: { documentId, clientCode },
      }),
      keepUnusedDataFor: 0, // Don't cache file downloads
      transformResponse: (response: Response) => response.blob(),
      transformErrorResponse: createApiErrorTransformer('ComplianceDownloadDocument', true),
    }),

    // ================================================================
    // OTP VERIFICATION ENDPOINTS FOR FINANCIAL PROOF
    // ================================================================

    // Send OTP for Financial Proof Upload
    sendFinancialProofOtp: builder.mutation<OtpSendResponse, OtpSendRequest>({
      query: ({ file, finProofType, clientCode }) => {
        const formData = new FormData();
        formData.append('action', 'send_otp');
        formData.append('finProofType', finProofType);
        formData.append('financialYearProof', file);

        return {
          url: `${BALANCE_SHEET_API_URL}/finproof/upload-financial-proof`,
          method: 'POST',
          body: formData,
          params: {
            source: 'ap',
            clientCode: clientCode,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to send OTP');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceSendOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('ComplianceSendOtp', 'OTP sent successfully.', true);
        } catch {}
      },
    }),

    // Verify OTP for Financial Proof Upload
    verifyFinancialProofOtp: builder.mutation<OtpVerifyResponse, OtpVerifyRequest>({
      query: ({ file, finProofType, otp, clientCode }) => {
        const formData = new FormData();
        formData.append('action', 'verify_otp');
        formData.append('finProofType', finProofType);
        formData.append('financialYearProof', file);
        formData.append('otp', otp);

        return {
          url: `${BALANCE_SHEET_API_URL}/finproof/upload-financial-proof`,
          method: 'POST',
          body: formData,
          params: {
            source: 'ap',
            clientCode: clientCode,
          },
        };
      },
      invalidatesTags: ['ComplianceFinancialProof', 'FileUpload'],
      transformResponse: (response: any) => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to verify OTP');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceVerifyOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('ComplianceVerifyOtp', 'OTP verified successfully.', true);
        } catch {}
      },
    }),

    // Resend OTP for Financial Proof Upload
    resendFinancialProofOtp: builder.mutation<OtpSendResponse, OtpResendRequest>({
      query: ({ file, finProofType, clientCode }) => {
        const formData = new FormData();
        formData.append('action', 'resend_otp');
        formData.append('finProofType', finProofType);
        formData.append('financialYearProof', file);

        return {
          url: `${BALANCE_SHEET_API_URL}/finproof/upload-financial-proof`,
          method: 'POST',
          body: formData,
          params: {
            source: 'ap',
            clientCode: clientCode,
          },
        };
      },
      transformResponse: (response: any) => {
        if (response.status >= 400) {
          throw new Error(response.message || 'Failed to resend OTP');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceResendOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('ComplianceResendOtp', 'OTP resent successfully.', true);
        } catch {}
      },
    }),
  }),
});

export const {
  useGetKycSummaryQuery,
  useGetModalTableQuery,
  useGetClientOverviewQuery,
  useGetClientSearchQuery,
  useGetClientDetailsQuery,
  useGetNomineeDetailsQuery,
  useLazyGetNomineeDetailsQuery,
  useLazyGetClientDetailsQuery,
  useGetActivityTrackingQuery,
  useGetActivityDetailsQuery,
  useGetKycApplicationsQuery,
  useGetKycModificationListQuery,
  useLazyGetKycModificationListQuery,
  useLazyDownloadKycModificationLogsQuery,
  useGetKycModificationDetailQuery,
  useGetReKycStatusQuery,
  useGetEKycStatusQuery,
  useGetReKycClientDetailsQuery,
  useGetEKycClientDetailsQuery,
  // Compliance Management Hooks
  useGetBalanceSheetComplianceQuery,
  useGetFinancialProofComplianceQuery,
  useUploadComplianceDocumentMutation,
  useUploadBalanceSheetDocumentMutation,
  useLazyDownloadComplianceDocumentQuery,
  // OTP Verification Hooks
  useSendFinancialProofOtpMutation,
  useVerifyFinancialProofOtpMutation,
  useResendFinancialProofOtpMutation,
} = kycApi;

/**
 * ================================================================
 * END OF SERVICE: KYCManagementService
 * ================================================================
 *
 * @exports          kycApi and all KYC-related query hooks
 * @dependencies     RTK Query, base query configuration, comprehensive KYC types
 * @usedBy           KYC components, pages, and management interfaces
 *
 * @maintenance
 * - Cache strategy: Different TTL settings based on data volatility
 * - Error handling: Consistent global error processing
 * - Performance: Optimized tag-based cache invalidation
 *
 * @security
 * - Authentication: Handled by base query configuration
 * - Data validation: Server response validation in all endpoints
 * - Error exposure: Safe error messages via global error handler
 *
 * ================================================================
 */
